/**
 * Cookie Consent（GDPR，仅海外构建生效）
 *
 * 同意的主体是「这台浏览器」而不是账号：选择只存 localStorage、不上后端，
 * 与登录态无关 —— 换设备/清缓存后重新弹一次是合规且预期的行为。
 *
 * 口径（与产品确认过的默认）：
 * - 「数据分析 Cookie」指 Mixpanel（会写 mp_* 的 cookie/storage），受本闸门控制；
 * - Sentry 错误监控归「保障服务运行的必要项」，不受 Reject 影响；
 * - 登录 token、语言偏好等必要项一律不受影响。
 */
import { IS_CHINA } from '@/config'

const STORAGE_KEY = 'cookie_consent'

// Cookies Policy 文本发生实质变更时 +1：所有存量选择立即失效，用户会重新看到弹窗
export const CONSENT_VERSION = 1

export const CONSENT_CHOICE = {
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
}

/** 读取已保存的选择；无记录 / 版本过期 / 数据损坏统一按「没选过」处理（会重新弹） */
export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (!data || data.version !== CONSENT_VERSION) return null
    if (data.choice !== CONSENT_CHOICE.ACCEPTED && data.choice !== CONSENT_CHOICE.REJECTED) {
      return null
    }
    return data
  } catch (e) {
    return null
  }
}

export function saveConsent(choice) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      // ts 留作将来「同意有效期 12 个月后重新征询」用，当前不启用
      JSON.stringify({ choice, version: CONSENT_VERSION, ts: Date.now() })
    )
  } catch (e) {
    // 隐私模式等场景写不进去：下次进来还会弹，可接受
  }
}

/**
 * 数据分析是否被允许启用。
 * 国内构建没有 GDPR 弹窗，一律放行（国内埋点走自己的合规流程）；
 * 海外是 opt-in 制：只有明确点过 Accept 才放行，「弹窗还没点」= 不放行。
 */
export function analyticsAllowed() {
  if (IS_CHINA) return true
  const consent = getConsent()
  return !!consent && consent.choice === CONSENT_CHOICE.ACCEPTED
}

/**
 * Reject 时清掉 Mixpanel 的残留（此前 Accept 过、现在改主意的场景）：
 * cookie（当前域 + 父域各清一次，Mixpanel 默认种在主域）和 localStorage。
 *
 * 前缀要同时匹配 mp_ 和 __mp：项目的 Mixpanel 开了 batch_requests，
 * 未发送的事件队列持久化在 localStorage 的 __mpq_<token>_* 键下（内含
 * distinct_id 和事件属性），只清 mp_ 会把这些排队数据留在浏览器里。
 */
function isMixpanelKey(name) {
  return name.indexOf('mp_') === 0 || name.indexOf('__mp') === 0
}

export function clearAnalyticsTraces() {
  try {
    const expire = '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'
    const parentDomain = location.hostname.replace(/^[^.]+/, '')
    document.cookie.split(';').forEach((item) => {
      const name = item.split('=')[0].trim()
      if (isMixpanelKey(name)) {
        document.cookie = name + expire
        if (parentDomain) {
          document.cookie = name + expire + '; domain=' + parentDomain
        }
      }
    })
    Object.keys(localStorage).forEach((key) => {
      if (isMixpanelKey(key)) localStorage.removeItem(key)
    })
  } catch (e) {
    // 清理失败不影响主流程：闸门已关，不会再写新的
  }
}
